import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import _ from 'lodash'
import Apis from '@/Apis/_'
import { TipoReservaSalaEnum, Hospital, SituacaoEnum, MotivoTransferenciaReserva } from '@/types/_'
import { getUser, handleCalendarResources, getSingleDate, handleCalendarEvents, createLinearGradientByTimeDifference, timeout, classByComplexidadeReservaSala, classByTipoReservaSala } from '@/utils/common'

interface MapaCirurgicoGradeCentroCirurgico {
  id?: string,
  nome?: string,
  somenteSalas: boolean,
  salas: MapaCirurgicoGradeSala[],
}

interface MapaCirurgicoGradeSala {
  id: string,
  nome: string,
  horaInicio: string,
  horaFim: string,
  tempoDePreparacao: number,
  salaCirurgicaHisId?: string,
  reservas: MapaCirurgicoGradeReserva[],
}

interface MapaCirurgicoGradeReserva {
  id?: string,
  inicio: string,
  fim: string,
  salaCirurgicaId: string,
  salaCirurgicaHisId: string,
  solicitacaoId?: string,
  medicoSolicitanteId?: string,
  medicoNome: string,
  pacienteNome?: string,
  convenioNome?: string,
  procedimentoPrincipalDescricao?: string,
  procedimentoPrincipalCodigo?: string,
  tempoCirurgia: number,
  tempoPreparacaoSalaCirurgica?: number,
  dataAgendamentoVirtual: boolean,
  numeroAgendamento: string,
  solicitacaoStatusSituacao: SituacaoEnum,
  tipoReservaSala: TipoReservaSalaEnum,
}

interface MapaCirurgicoGradeMedicoSchedule {
  carater: string,
  horario: string,
  medicoNome: string,
  medicoSolicitanteId: string,
  pacienteNome: string,
  salaCirurgicaNome: string,
  solicitacaoStatusSituacao: string,
}

@Module({ namespaced: true, name: 'BackofficeMapaCirurgicoGradeStore' })
export default class BackofficeMapaCirurgicoGradeStore extends VuexModule {
  public resources: any[] = []
  public events: any[] = []
  public filteredEvents: any[] = []
  public filteredState: boolean = false
  public filteredField: string = ''
  public eventByLabelType: 'Medico' | 'Paciente' = 'Medico'
  public eventByView: 'default' | 'complexidade' = 'default'
  public shouldGroupFields: boolean = false
  public template: any = { formulario: {}, hospital: {}, porConvenio: [] }

  @Mutation
  setShouldGroupFields(value: boolean) {
    this.shouldGroupFields = value
  }

  @Mutation
  setFilteredState(value: boolean) {
    this.filteredState = value
  }

  @Mutation
  setResources(value: any[]) {
    this.resources = value
  }

  @Mutation
  clearResources() {
    this.resources = []
  }

  @Mutation
  setEvents(value: any[]) {
    this.events = value
    this.filteredEvents = value
  }

  @Mutation
  clearEvents() {
    this.events = []
    this.filteredEvents = []
  }

  @Mutation
  filter(filterParams: { campo: string, valor: string }) {
    if (this.filteredState && this.filteredField === filterParams.valor) {
      this.filteredEvents = this.events.map((event: any) => {
        const newEvent = event

        if (newEvent.classNames.includes('finx-calendar__event--fade')) {
          newEvent.classNames.splice(newEvent.classNames.indexOf('finx-calendar__event--fade'), 1)
          if (newEvent.gradient) {
            newEvent.gradient = createLinearGradientByTimeDifference(newEvent.start, newEvent.end, newEvent.tempoDePreparacao, newEvent.tipoReservaSala)
          }
        }

        return newEvent
      })
      this.filteredState = false
      this.filteredField = ''
    } else {
      this.filteredState = true
      this.filteredField = filterParams.valor

      this.filteredEvents = this.events.map((event: any) => {
        const newEvent = event

        if (newEvent[filterParams.campo] === filterParams.valor && newEvent.classNames.includes('finx-calendar__event--fade')) {
          newEvent.classNames.splice(newEvent.classNames.indexOf('finx-calendar__event--fade'), 1)
        }

        if (newEvent[filterParams.campo] !== filterParams.valor) {
          if (!newEvent.classNames.includes('finx-calendar__event--fade')) {
            newEvent.classNames = [...newEvent.classNames, 'finx-calendar__event--fade']
            if (newEvent.gradient && filterParams.valor !== 'Cirurgia') {
              newEvent.gradient = 'transparent'
            }
          }
        }
        return newEvent
      })
    }
  }

  @Mutation
  resetFilter() {
    this.filteredEvents = this.events.map((event: any) => {
      const newEvent = event

      if (newEvent.classNames.includes('finx-calendar__event--fade')) {
        newEvent.classNames.splice(newEvent.classNames.indexOf('finx-calendar__event--fade'), 1)
      }

      return newEvent
    })
    this.filteredState = false
    this.filteredField = ''
  }

  @Mutation
  setEventByLabelType(value: 'Medico' | 'Paciente') {
    this.eventByLabelType = value

    this.events = this.events.map((event: any) => {
      const ev = { ...event }
      ev.title = ev.tipoReservaSala === TipoReservaSalaEnum.AgendamentoApenasNoHis
        ? ev.title
        : event[this.eventByLabelType === 'Paciente' ? 'pacienteNome' : 'medicoNome']
      return ev
    })

    this.filteredEvents = this.filteredEvents.map((event: any) => {
      const ev = { ...event }
      ev.title = ev.tipoReservaSala === TipoReservaSalaEnum.AgendamentoApenasNoHis
        ? ev.title
        : event[this.eventByLabelType === 'Paciente' ? 'pacienteNome' : 'medicoNome']
      return ev
    })
  }

  @Mutation
  setEventByView(value: 'default' | 'complexidade') {
    this.eventByView = value

    if (value === 'default') {
      this.events = this.events.map((event: any) => {
        event.classNames = [classByTipoReservaSala(event.tipoReservaSala)]
        return event
      })

      this.filteredEvents = this.filteredEvents.map((event: any) => {
        event.classNames = [classByTipoReservaSala(event.tipoReservaSala)]
        return event
      })
    } else {
      this.events = this.events.map((event: any) => {
        event.classNames = [classByComplexidadeReservaSala(event.tipoReservaSala)]
        return event
      })

      this.filteredEvents = this.filteredEvents.map((event: any) => {
        event.classNames = [classByComplexidadeReservaSala(event.tipoReservaSala)]
        return event
      })
    }
  }

  @Mutation
  setTemplate(template: any) {
    this.template = template
  }

  @Mutation
  updateEvent(clientEvent: any) {
    const { event, newResource } = clientEvent
    const newEvent = {
      ...event.extendedProps,
      id: event.extendedProps.solicitacaoId,
      resourceId: newResource ? newResource.id : event.extendedProps.salaCirurgicaId,
      salaCirurgicaId: newResource ? newResource.extendedProps.salaCirurgicaId : event.extendedProps.salaCirurgicaId,
      salaCirurgicaNome: newResource ? newResource.extendedProps.salaCirurgicaNome : event.extendedProps.salaCirurgicaNome,
      // eslint-disable-next-line
      centroCirurgicoId: this.shouldGroupFields ? (newResource ? newResource : event).extendedProps.centroCirurgicoId : undefined,
      // eslint-disable-next-line
      centroCirurgicoNome: this.shouldGroupFields ? (newResource ? newResource : event).extendedProps.centroCirurgicoNome : undefined,
      start: event.startStr,
      end: event.endStr,
      gradient: createLinearGradientByTimeDifference(event.startStr, event.endStr, event.extendedProps.tempoPreparacaoSalaCirurgica, event.extendedProps.tipoReservaSala),
      title: event.title,
      editable: ![TipoReservaSalaEnum.AgendamentoApenasNoHis, TipoReservaSalaEnum.PreAgendamento].includes(event.extendedProps.tipoReservaSala),
      durationEditable: false,
      resourceEditable: ![TipoReservaSalaEnum.AgendamentoApenasNoHis, TipoReservaSalaEnum.PreAgendamento].includes(event.extendedProps.tipoReservaSala),
      borderColor: event.borderColor,
      classNames: event.classNames,
    }

    Object.entries(newEvent).forEach(([key, value]) => {
      if (['', null, undefined].includes(value as any)) delete newEvent[key]
    })

    this.events = [...this.events.filter((ev: any) => ev.id !== newEvent.id), newEvent]
    this.filteredEvents = [...this.filteredEvents.filter((ev: any) => ev.id !== newEvent.id), newEvent]
  }


  /** HOSPITAIS */
  private hospitalDefault: Hospital = {
    id: '',
    nome: '',
    hospitalDataVirtualFazReservaDeSala: false,
    hospitalInformaMotivoDeTransferenciaReserva: false,
  }

  public hospital: Hospital = { ...this.hospitalDefault }
  public hospitais: Hospital[] = []

  @Mutation
  setHospital(value: Hospital) {
    this.hospital = value
  }

  @Mutation
  setHospitais(value: Hospital[]) {
    this.hospitais = value
  }

  @Mutation
  clearHospital() {
    this.hospital = { ...this.hospitalDefault }
  }

  @Mutation
  clearHospitais() {
    this.hospitais = []
  }

  @Action({ commit: 'setHospitais' })
  async getAllHospitais() {
    const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getHospitalCurrentUser()
    if (request.success) return request.data
    return []
  }

  /** MOTIVO DE TRANSFERÊNCIA */
  private motivoTransferenciaReservaDefault: MotivoTransferenciaReserva = {
    descricao: '',
    id: '',
    hisId: '',
    medicoVisualiza: false,
    status: '',
  }

  public motivoTransferenciaReserva: MotivoTransferenciaReserva = { ...this.motivoTransferenciaReservaDefault }
  public motivosTransferenciaReserva: MotivoTransferenciaReserva[] = []

  @Mutation
  setMotivosTransferenciaReserva(itens: MotivoTransferenciaReserva[]) {
    this.motivosTransferenciaReserva = itens
  }

  @Mutation
  setMotivoTransferenciaReserva(item: MotivoTransferenciaReserva) {
    this.motivoTransferenciaReserva = item
  }

  @Mutation
  clearMotivoTransferenciaReserva() {
    this.motivoTransferenciaReserva = { ...this.motivoTransferenciaReservaDefault }
  }

  @Mutation
  clearMotivosTransferenciaReserva() {
    this.motivosTransferenciaReserva = []
  }

  @Action
  async getMotivosTransferenciaReservaByHospital() {
    try {
      const response = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getMotivosTransferenciaReservaByHospital({ hospitalId: this.hospital.id })
      if (response.success) this.context.commit('setMotivosTransferenciaReserva', response.data as MotivoTransferenciaReserva[])
      return response
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async backofficeAgendamentoAlteraReserva(params: any) {
    try {
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.backofficeAgendamentoAlteraReserva(params.solicitacaoId, params.payload)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  /** Events and Resources */
  public currentDate: string = getSingleDate()

  @Mutation
  setCurrentDate(value: string) {
    this.currentDate = value || getSingleDate()
  }

  @Action
  async getGradeMapaCirurgico() {
    try {
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getGradeMapaCirurgico({
        hospitalId: this.hospital.id,
        data: this.currentDate,
      })

      if (request.success) {
        this.context.commit('setShouldGroupFields', (request.data && 'somenteSalas' in request.data) ? !request.data.somenteSalas as Boolean : false)
        this.context.commit('setResources', handleCalendarResources(request.data))
        this.context.commit('setEvents', handleCalendarEvents(request.data, this.hospital.id))
        this.context.commit('setEventByLabelType', this.eventByLabelType)
        this.context.commit('setEventByView', this.eventByView)
        this.context.dispatch('getTemplateHospital')
      }

      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição' }
    }
  }

  @Action
  async getTemplateHospital() {
    try {
      const request = await Apis.Agendamento.SolicitacaoCirurgiaApi.getTemplateHospital(this.hospital.id)
      if (request.success) {
        this.context.commit('setTemplate', JSON.parse(request.data.template))
      }

      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição' }
    }
  }

  /** PESQUISA DE GRADE */
  public medicoSchedule: MapaCirurgicoGradeMedicoSchedule[] = []

  @Mutation
  setMedicoSchedule(value: MapaCirurgicoGradeMedicoSchedule[]) {
    this.medicoSchedule = value
  }

  @Mutation
  clearMedicoSchedule() {
    this.medicoSchedule = []
  }

  @Action
  async getPesquisaGradeMapaCirurgico() {
    try {
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getPesquisaGradeMapaCirurgico({
        hospitalId: this.hospital.id,
        medicoSolicitanteId: this.medicoSolicitanteId,
      })
      if (request.success) this.context.commit('setMedicoSchedule', request.data as MapaCirurgicoGradeMedicoSchedule[] || [])
      return request
    } catch (error) {
      this.context.commit('setMedicoSchedule', [])
      return { success: false, message: 'Ocorreu um erro na requisição' }
    }
  }

  @Action
  async getPesquisaGradeMapaCirurgicoWithParams({ hospitalId, medicoSolicitanteId }: { hospitalId: string, medicoSolicitanteId: string }) {
    try {
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getPesquisaGradeMapaCirurgico({ hospitalId, medicoSolicitanteId })
      if (request.success) this.context.commit('setMedicoSchedule', request.data as MapaCirurgicoGradeMedicoSchedule[] || [])
      return request
    } catch (error) {
      this.context.commit('setMedicoSchedule', [])
      return { success: false, message: 'Ocorreu um erro na requisição' }
    }
  }

  /** MÉDICOS */
  public medicos: Array<{ id: string, nome: string }> = []
  public medicoSolicitanteId: string = ''

  @Mutation
  setMedicos(value: Array<{ id: string, nome: string }>) {
    this.medicos = value
  }

  @Mutation
  clearMedicos() {
    this.medicos = []
  }

  @Mutation
  setMedicoSolicitanteId(value: string) {
    this.medicoSolicitanteId = value
  }

  @Mutation
  clearMedicoSolicitanteId() {
    this.medicoSolicitanteId = ''
  }

  @Mutation
  clearTemplate() {
    this.template = { formulario: {}, hospital: {}, porConvenio: [] }
  }

  @Action
  async getAllMedicosByHospitalId() {
    try {
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getAllMedicosByHospitalId({
        usuarioId: getUser().profile.sub,
        hospitalId: this.hospital.id,
      })
      if (request.success) this.context.commit('setMedicos', request.data as Array<{ id: string, nome: string }> || [])
      return request
    } catch (error) {
      this.context.commit('setMedicos', [])
      return { success: false, message: 'Ocorreu um erro na requisição' }
    }
  }
}
